import { ThemeEvents } from '@theme/events';
import {
  onSendingChange,
  sendingAllowed,
  trackerReady,
  withTracker,
} from '@theme/searchspring-beacon';
import { forgetShopper } from '@theme/searchspring-consent';

/**
 * Searchspring platform events — product views, shopper login and cart
 * changes — reported with the combined-listing parent's IDs, which is what
 * Searchspring indexes. Shopify's own events, and the Searchspring app pixel
 * built on them, only know the child product and variant, so these come
 * from the theme. Orders come from the "Searchspring orders" custom pixel:
 * pixels/searchspring-orders.js.
 *
 * Product identity travels as "parentId:uid:sku", rendered by
 * snippets/searchspring-product-ids.liquid. Cart lines carry it in the
 * hidden _ss_parent property; cart changes are found by diffing /cart.js
 * against the last snapshot, whatever changed it.
 *
 * Events go through withTracker() from assets/searchspring-beacon.js, which
 * sends them only with analytics consent, holding them until the tracker has
 * loaded. The cart is tracked only while analytics is allowed: without
 * consent nothing is reported, so /cart.js isn't fetched, cart changes aren't
 * diffed and the snapshot isn't stored. Tracking also stops if Beacon.js
 * fails to load, since there would be nothing to report through. Stopping
 * only pauses: nothing stored is cleared, and the next change re-reads
 * /cart.js.
 */

/** Hidden line-item property carrying "parentId:uid:sku" (added at add-to-cart). */
const PARENT_PROPERTY = '_ss_parent';

/**
 * localStorage key for the cart as last reported, shared by every tab so a
 * change made in one isn't reported again by another.
 */
const SNAPSHOT_KEY = 'ssPlatformCart';

/** @typedef {{ parentId: string, uid: string, sku?: string }} ProductIds */

/**
 * @param {unknown} value - "parentId:uid:sku"; sku may be empty
 * @returns {ProductIds | null}
 */
function parseIds(value) {
  const [parentId, uid, sku] = String(value || '').split(':');
  if (!parentId || !uid) return null;
  return sku ? { parentId, uid, sku } : { parentId, uid };
}

/**
 * @typedef {ProductIds & { qty: number, price: number }} CartResult
 * @typedef {Record<string, CartResult>} CartSnapshot - keyed by cart line key
 */

/**
 * Rendered by snippets/searchspring-platform-data.liquid.
 * @returns {{ product?: string, customerId?: string | number | null, cartSignature?: string }}
 */
function pageData() {
  try {
    return JSON.parse(document.getElementById('searchspring-platform-data')?.textContent || '{}');
  } catch {
    return {};
  }
}

/**
 * A /cart.js line as a Beacon cart result. Lines added before `_ss_parent`
 * existed fall back to Shopify's own IDs.
 * @param {any} item
 * @returns {CartResult}
 */
function toCartResult(item) {
  const ids = parseIds(item.properties?.[PARENT_PROPERTY]) || {
    parentId: String(item.product_id),
    uid: String(item.variant_id),
    ...(item.sku ? { sku: String(item.sku) } : {}),
  };
  return { ...ids, qty: item.quantity, price: item.price / 100 };
}

/**
 * @param {any} cart - a /cart.js response
 * @returns {CartSnapshot}
 */
function snapshotOf(cart) {
  /** @type {CartSnapshot} */
  const lines = {};
  for (const item of cart.items || []) lines[item.key] = toCartResult(item);
  return lines;
}

/**
 * Per-line quantity changes between two snapshots.
 * @param {CartSnapshot} before
 * @param {CartSnapshot} after
 * @returns {{ added: CartResult[], removed: CartResult[] }}
 */
function diffCarts(before, after) {
  /** @type {CartResult[]} */
  const added = [];
  /** @type {CartResult[]} */
  const removed = [];

  for (const [key, line] of Object.entries(after)) {
    const delta = line.qty - (before[key]?.qty || 0);
    if (delta > 0) added.push({ ...line, qty: delta });
    if (delta < 0) removed.push({ ...line, qty: -delta });
  }
  for (const [key, line] of Object.entries(before)) {
    if (!(key in after)) removed.push(line);
  }

  return { added, removed };
}

/**
 * "key=quantity" pairs in a stable order, comparable with the cartSignature
 * snippets/searchspring-platform-data.liquid renders.
 * @param {string[]} pairs
 * @returns {string}
 */
function signatureOf(pairs) {
  return pairs.filter(Boolean).sort().join(',');
}

/**
 * The last snapshot read or written on this page, for when localStorage is
 * blocked: without it every change would be reported against an empty cart.
 * @type {CartSnapshot | null}
 */
let memorySnapshot = null;

/** @returns {CartSnapshot | null} */
function readSnapshot() {
  try {
    memorySnapshot = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null') || memorySnapshot;
  } catch {
    // Storage blocked: fall back to memory.
  }
  return memorySnapshot;
}

/** @param {CartSnapshot} snapshot */
function writeSnapshot(snapshot) {
  memorySnapshot = snapshot;

  // Stored only while sending is allowed, e.g. not if consent was withdrawn,
  // here or in another tab, while /cart.js loaded. clearSearchspringStorage()
  // removes it on a refusal.
  if (!sendingAllowed()) return;

  try {
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full or blocked: readSnapshot falls back to memorySnapshot.
  }
}

/** @returns {Promise<any | null>} */
async function fetchCart() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

/**
 * Make the snapshot match the cart without reporting anything, before the
 * first change is diffed. A snapshot out of step with the cart this page was
 * served with — for any reason: an order emptied the cart, the snapshot was
 * cleared, or the cart changed while it wasn't tracked — is re-read from
 * /cart.js.
 * @param {string | null} served - cartSignature from the page data, or null
 *   when the snapshot can't be trusted against it (the cart changed while
 *   untracked, or tracking was withdrawn), so the cart must be fetched.
 */
async function syncSnapshot(served) {
  const snapshot = readSnapshot();
  const lines = Object.entries(snapshot || {});

  if (served !== null) {
    // Served empty, with nothing on record: there's nothing to fetch.
    if (!served && !lines.length) {
      writeSnapshot({});
      withTracker((beacon) => beacon.storage?.cart?.set([]));
      return;
    }

    const current = signatureOf(lines.map(([key, line]) => `${key}=${line.qty}`));
    if (snapshot && current === signatureOf(served.split(','))) return;
  }

  const cart = await fetchCart();
  if (!cart) return;

  const fresh = snapshotOf(cart);
  writeSnapshot(fresh);

  // Keep Beacon.js's cart history (ssCartProducts, used for personalization) in step.
  withTracker((beacon) => beacon.storage?.cart?.set(Object.values(fresh)));
}

/** Report what changed in the cart since the last snapshot. */
async function reportCartChange() {
  const cart = await fetchCart();
  if (!cart) return;

  const after = snapshotOf(cart);
  const { added, removed } = diffCarts(readSnapshot() || {}, after);
  writeSnapshot(after);

  if (!added.length && !removed.length) return;

  // `cart` is the whole cart after the change; Beacon.js stores it as ssCartProducts.
  const full = Object.values(after);
  withTracker((beacon) => {
    if (added.length) beacon.events.cart.add({ data: { results: added, cart: full } });
    if (removed.length) beacon.events.cart.remove({ data: { results: removed, cart: full } });
  });
}

function reportPageEvents() {
  const data = pageData();
  const product = parseIds(data.product);

  if (!data.customerId && !product) return;

  withTracker((beacon) => {
    // Beacon.js sends shopper/login only when the stored shopper ID changes.
    if (data.customerId) beacon.setShopperId(String(data.customerId));

    // Beacon.js also adds the product to ssViewedProducts.
    if (product) beacon.events.product.pageView({ data: { result: product } });
  });
}

if (!(/** @type {any} */ (window).Shopify?.designMode)) {
  // Logged out: forget the shopper ID Beacon.js stored, before it reads it
  // for this page's events. A deletion, so it doesn't wait for consent. If
  // Beacon.js already read it, clear its in-memory copy too.
  if (!pageData().customerId) {
    forgetShopper();
    withTracker((beacon) => beacon.updateContext?.('shopperId', ''));
  }

  try {
    reportPageEvents();
  } catch {
    // Analytics must never break the page.
  }

  // One change at a time, each diffed against the snapshot the previous one
  // left. It never waits on consent: until the cart is tracked, changes are
  // skipped rather than queued.
  let cartQueue = Promise.resolve();

  /** Whether cart changes are being diffed and reported. */
  let tracking = false;

  /**
   * Whether the snapshot may be out of step with the cart this page was
   * served with: the cart changed while it wasn't tracked, or tracking was
   * withdrawn, after which the memory copy can't be trusted.
   */
  let missedChange = false;

  /** Starts tracking the cart, with a silent sync, once analytics is allowed. */
  const startTracking = () => {
    if (tracking) return;
    tracking = true;

    const served = missedChange ? null : pageData().cartSignature || '';
    missedChange = false;
    cartQueue = cartQueue.then(() => syncSnapshot(served)).catch(() => {});
  };

  // From the moment analytics is allowed, at page load or when the visitor
  // accepts on this page, not only once Beacon.js has loaded: a change made
  // before it loads is diffed now and sent when it does. A withdrawal, here
  // or in another tab, stops tracking, and a later acceptance re-reads
  // /cart.js.
  //
  // trackerReady(), not consentReady(): sendingAllowed() is only an answer
  // once the beacon module has acted on the same consent decision, which can
  // stop the page (a withdrawal made while it sat in the HTTP cache).
  trackerReady()
    .then(() => {
      if (sendingAllowed()) startTracking();
    })
    .catch(() => {
      // Analytics must never break the page.
    });
  onSendingChange((sending) => {
    if (sending) {
      startTracking();
    } else {
      tracking = false;
      missedChange = true;
    }
  });

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    if (!tracking) {
      missedChange = true;
      return;
    }
    cartQueue = cartQueue.then(reportCartChange).catch(() => {});
  });
}
